/**
 * A FELTÖLTÉS KIMENETE, EGY MONDATBAN — a lábsávban, nem az oldal tetején.
 *
 * Telefonon a lista hosszú lehet, és a feltöltés a lábsávból is indulhat. Egy
 * felül megjelenő nyugtázást a görgetés eltüntetne, és az ügyfél nem tudná
 * meg, sikerült-e — abból pedig újabb feltöltés lesz ugyanabból a fájlból.
 *
 * A „MÁR MEGKAPTUK" `status`, NEM `alert`. Nem hiba (`2.6/20`), tehát a
 * képernyőolvasó se szakítsa félbe vele az olvasást.
 *
 * ── AZ EGYFÁJLOS MONDAT MEGMARAD ─────────────────────────────────────
 *
 * „Megkaptuk, köszönjük." rövidebb és emberibb, mint „1 bizonylatot
 * megkaptunk". A köteges mondatok MELLÉ kerültek, nem helyettük — a leggyakoribb
 * eset (egy fénykép telefonról) nem lehet a legesetlenebb szöveg.
 *
 * ── A RÉSZLEGES SIKERT KI KELL MONDANI ───────────────────────────────
 *
 * Ha öt részletből a harmadik bukott el, a hibaüzenet önmagában HAZUDNA: az
 * ügyfél azt hinné, semmi nem érkezett meg, és újraküldené az egészet. Ezért
 * a hibamondat elé odakerül, mennyi jutott át.
 *
 * ── AMI KIMARADT, AZT NÉVVEL MONDJUK ─────────────────────────────────
 *
 * A túl nagy és a rossz típusú fájl el sem indul (`chunking.ts`). Ha csak
 * annyit írnánk ki, hogy „néhány fájl kimaradt", az ügyfél nem tudná, melyiket
 * kell külön elküldenie — és a néma elhagyás még rosszabb: megnyugodna.
 */

import { useTranslation } from 'react-i18next';
import type { SkippedFile, SkipReason } from './chunking';
import { CANCELLED_KEY, type UploadOutcome } from './useUploader';

export function UploadNotice({
  outcome,
  onCancel,
}: {
  outcome: UploadOutcome;
  /** A „Mégsem" — csak futás közben látszik, és csak ha van mit megszakítani. */
  onCancel?: () => void;
}) {
  const { t } = useTranslation();

  switch (outcome.kind) {
    case 'idle':
      return null;

    case 'busy':
      return (
        <p className="notice meta notice-progress">
          <span>
            {outcome.total > 1
              ? t('upload.inProgressCount', { done: outcome.done, total: outcome.total })
              : t('upload.inProgress')}
          </span>
          {onCancel ? (
            // NEM DÍSZ. Egy tévedésből elindított nagy köteg alól enélkül
            // nincs kiút: a felület a végéig letiltva marad.
            <button type="button" className="pick pick-link" onClick={onCancel}>
              {t('upload.cancel')}
            </button>
          ) : null}
        </p>
      );

    case 'settled':
      return <SettledNotice outcome={outcome} />;

    default: {
      const exhaustive: never = outcome;
      throw new Error(`Ismeretlen feltöltési kimenet: ${JSON.stringify(exhaustive)}`);
    }
  }
}

/**
 * A LEZÁRT FELTÖLTÉS — külön komponens, mert három egymástól független dolgot
 * mond el egyszerre: mi ment át, mi bukott el, és mi maradt ki.
 */
function SettledNotice({ outcome }: { outcome: Extract<UploadOutcome, { kind: 'settled' }> }) {
  const { t } = useTranslation();
  const { stored, known, skipped, failure } = outcome;
  const arrived = stored + known;

  return (
    <>
      {failure?.messageKey === CANCELLED_KEY ? (
        // A FELHASZNÁLÓ SAJÁT DÖNTÉSE NEM RIASZTÁS. `role="alert"`-tel a
        // képernyőolvasó félbeszakítaná az olvasást azért, hogy elmondja
        // neki, amit ő maga kért az előbb.
        <output className="notice meta">
          {arrived > 0
            ? `${t('upload.partial', { count: arrived })} ${t(CANCELLED_KEY)}`
            : t(CANCELLED_KEY)}
        </output>
      ) : failure ? (
        <p className="notice error" role="alert">
          {/* A szerver üzenete magyarul jön, és a korlát EGY helyen él — a
              felület sajátja csak akkor kell, ha a szerver nem mondott
              semmit (pl. a proxy vágta el a túl nagy kérést). */}
          {arrived > 0 ? `${t('upload.partial', { count: arrived })} ` : ''}
          {failure.serverMessage ?? t(failure.messageKey)}
        </p>
      ) : arrived > 0 || skipped.length === 0 ? (
        // `<output>`, nem `<p role="status">`: az `output` implicit szerepe
        // ugyanez, de a jelentés az ELEMBEN áll, nem egy attribútumban, amit
        // egy átfogalmazás elveszíthet.
        <output className="notice ok">{settledSentence(t, stored, known)}</output>
      ) : null}

      {/* A TÍPUS NEM RÖGZÜLT (#85) — a bizonylat megvan, ezért nem riasztás,
          hanem halk sor a teendővel. Ha hallgatnánk, az ügyfél azt hinné, a
          „Mi ez?"-re adott válasza ott áll. */}
      {outcome.undeclared > 0 ? (
        <output className="notice meta">
          {t('upload.typeNotSaved', { count: outcome.undeclared })}
        </output>
      ) : null}

      <SkippedLines skipped={skipped} />
    </>
  );
}

type Translate = ReturnType<typeof useTranslation>['t'];

function settledSentence(t: Translate, stored: number, known: number): string {
  if (stored > 0 && known > 0) {
    return t('upload.doneAndKnown', { stored, known });
  }
  if (stored > 0) {
    return stored === 1 ? t('upload.done') : t('upload.doneCount', { count: stored });
  }
  if (known > 0) {
    return known === 1 ? t('upload.alreadyHave') : t('upload.knownCount', { count: known });
  }

  // ELKÜLDTÜK, DE A VÁLASZ EGY SORT SEM SOROLT FEL. Nem hiba (a kérés 200-zal
  // tért vissza), viszont némán elmenni innen a legrosszabb kimenet: az
  // ügyfél feltöltött, és a felület nem mond SEMMIT. A „már megkaptuk" itt
  // HAZUDNA, a darabszám sem állítható — marad a semleges nyugta. Ugyanez a
  // döntés állt az egyfájlos változatban is, ott a `?? false` mögött.
  return t('upload.done');
}

/** Okonként egy sor, a fájlnevekkel — a felsorolás a lényeg, nem a szám. */
function SkippedLines({ skipped }: { skipped: SkippedFile[] }) {
  const { t } = useTranslation();

  if (skipped.length === 0) {
    return null;
  }

  const byReason: [SkipReason, string][] = [
    ['tooLarge', 'upload.skippedTooLarge'],
    ['wrongType', 'upload.skippedWrongType'],
    ['refused', 'upload.refused'],
  ];

  return (
    <>
      {byReason.map(([reason, messageKey]) => {
        const files = skipped.filter((item) => item.reason === reason);
        if (files.length === 0) {
          return null;
        }
        const names = files.map((item) => item.file.name).join(', ');

        // ELŐBB A MIÉRT, AZTÁN A NEVEK. A „nem fogadta el" önmagában nem
        // mondja meg, mit tegyen az ügyfél. A szerver mondata előbbre való a
        // miénknél (ő tudja az okot), de a 413 törzse gyakran ÜRES — a proxy
        // vágja el a kérést, mielőtt az app válaszolna —, ezért kell a saját
        // tartalék mondatunk is.
        const why =
          reason === 'refused'
            ? (files.find((item) => item.serverMessage)?.serverMessage ??
              t(files.find((item) => item.messageKey)?.messageKey ?? 'upload.failed'))
            : null;

        return (
          <p className="notice error" role="alert" key={reason}>
            {why ? `${why} ` : ''}
            {t(messageKey, { files: names })}
          </p>
        );
      })}
    </>
  );
}
